import { normalizeWhitespace } from "@/utils";
import type { RegisteredFilter } from "./OrgMemberTableSource";

/**
 * 멤버 디렉터리의 조회 축. URL 이 소유한다(rules.md §17). 디렉터리는 단일 GET 이다. 그래서 두 축
 * 모두 클라이언트에서 거른다(`OrgMemberTableSource`).
 */
export type MemberFilterState = {
  search: string;
  registered: RegisteredFilter;
};

export const ALL_REGISTERED: RegisteredFilter = "all";

export const EMPTY_MEMBER_FILTERS: MemberFilterState = { search: "", registered: ALL_REGISTERED };

export const REGISTERED_FILTER_OPTIONS: readonly { value: RegisteredFilter; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "registered", label: "가입" },
  { value: "unregistered", label: "미가입" },
];

export function isRegisteredFilter(value: string | null): value is RegisteredFilter {
  return REGISTERED_FILTER_OPTIONS.some((option) => option.value === value);
}

export function registeredFilterLabel(value: RegisteredFilter): string {
  return REGISTERED_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

const PARAM = { search: "search", registered: "registered" } as const;

export function readMemberFilters(params: URLSearchParams): MemberFilterState {
  const registered = params.get(PARAM.registered);
  return {
    search: normalizeWhitespace(params.get(PARAM.search) ?? ""),
    // 모르는 값은 전체로 바꾼다. 그래서 오래된 딥링크가 빈 목록을 만들지 않는다.
    registered: isRegisteredFilter(registered) ? registered : ALL_REGISTERED,
  };
}

// 자기 키만 지우고 다시 쓴다. 탭(`tab`) 같은 다른 키는 바꾸지 않는다. 입력은 변경하지 않는다.
export function writeMemberFilters(
  params: URLSearchParams,
  state: MemberFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const search = normalizeWhitespace(state.search);
  if (search === "") {
    next.delete(PARAM.search);
  } else {
    next.set(PARAM.search, search);
  }
  if (state.registered === ALL_REGISTERED) {
    next.delete(PARAM.registered);
  } else {
    next.set(PARAM.registered, state.registered);
  }
  return next;
}

export type MemberFilterChip = {
  axis: keyof MemberFilterState;
  label: string;
};

export function memberFilterChips(state: MemberFilterState): MemberFilterChip[] {
  const chips: MemberFilterChip[] = [];
  if (state.registered !== ALL_REGISTERED) {
    chips.push({
      axis: "registered",
      label: `가입 여부: ${registeredFilterLabel(state.registered)}`,
    });
  }
  if (state.search !== "") {
    chips.push({ axis: "search", label: `검색: ${state.search}` });
  }
  return chips;
}

// 칩 ✕. 그 축만 기본값으로 되돌린다.
export function clearMemberFilterAxis(
  state: MemberFilterState,
  axis: keyof MemberFilterState,
): MemberFilterState {
  return { ...state, [axis]: EMPTY_MEMBER_FILTERS[axis] };
}
